import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TheShowPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [experiences, setExperiences] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const res = await fetch(`${API_URL}/api/showroom/the-show`, { credentials: 'include' });
        if (res.status === 401) {
          navigate(`/admin/login?redirect=/the-show/experiences`, { replace: true });
          return;
        }
        if (!res.ok) throw new Error('Failed to load experiences');
        const data = await res.json();
        const items = data.experiences || [];
        if (items.length === 1) {
          navigate(`/the-show/${items[0].id}`, { replace: true });
          return;
        }
        setExperiences(items);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchExperiences();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060d1b] flex items-center justify-center" data-testid="the-show-loading">
        <Loader2 className="w-8 h-8 text-[#0077FF] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#060d1b] flex items-center justify-center px-4" data-testid="the-show-error">
        <div className="text-center">
          <p className="text-white/60 text-sm mb-4">{error}</p>
          <button onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-[#0077FF] text-white text-sm font-medium hover:bg-[#0066DD] transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (experiences.length === 0) {
    return (
      <div className="min-h-screen bg-[#060d1b] flex items-center justify-center px-4" data-testid="the-show-empty">
        <p className="text-white/40 text-sm">No experiences available.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060d1b] flex items-center justify-center px-4" data-testid="the-show-picker">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <PlayCircle className="w-10 h-10 text-[#0077FF] mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white tracking-tight">The Show</h1>
          <p className="text-white/40 text-sm mt-1">Select an experience to launch</p>
        </div>
        <div className="space-y-2">
          {experiences.map((exp) => (
            <button key={exp.id} onClick={() => navigate(`/the-show/${exp.id}`)}
              className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border border-white/[0.06] bg-white/[0.03] hover:bg-[#0077FF]/10 hover:border-[#0077FF]/25 transition-all duration-200 group"
              data-testid={`the-show-exp-${exp.id}`}>
              <PlayCircle className="w-5 h-5 text-white/30 group-hover:text-[#0077FF] transition-colors flex-shrink-0" />
              <div className="text-left flex-1 min-w-0">
                <p className="text-sm font-semibold text-white/90 group-hover:text-white truncate">{exp.name}</p>
                {exp.vertical && <p className="text-[11px] text-white/30 mt-0.5 capitalize">{exp.vertical}</p>}
              </div>
              <svg className="w-4 h-4 text-white/20 group-hover:text-[#0077FF]/60 transition-colors flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TheShowPage;
